import { inject, injectable } from 'inversify'
import type { RestrictionPageQuery, TmRoutes } from '@/services/route/routerTypes'
import { SERVICE_TYPES } from '@/core/container/types'
import type RouterService from '@/services/route/routerService'

export type RestrictionPageUserData = {
  fullName?: string | null
  email?: string | null
}

@injectable()
export default class RestrictionPagesRouterService {
  private readonly loginPersonalRoute: TmRoutes = {
    name: 'auth.login.personal',
  }

  private readonly reopenAccountRoute: TmRoutes = {
    name: 'auth.login.reopenAccount',
  }

  private readonly accountClosedRoute: TmRoutes = {
    name: 'auth.login.accountClosed',
  }

  constructor(@inject(SERVICE_TYPES.RouterService) protected readonly routerService: RouterService) {}

  public goToAccountSuspendedRoute(userData?: RestrictionPageUserData) {
    return this.routerService.replace({ name: 'auth.login.suspended', query: this.toQuery(userData) })
  }

  public goToLoginPersonalRoute(userData?: RestrictionPageUserData) {
    return this.routerService.replace({ ...this.loginPersonalRoute, query: this.toQuery(userData) })
  }

  public goToUnavailableLocationRoute(strategy: 'push' | 'replace' = 'push', userData?: RestrictionPageUserData) {
    const route: TmRoutes = { name: 'auth.signup.unavailable', query: this.toQuery(userData) }
    if (strategy === 'replace') {
      return this.routerService.replace(route)
    }
    return this.routerService.push(route)
  }

  public goToNotApprovedRoute(userData?: RestrictionPageUserData) {
    return this.routerService.push({ name: 'auth.signup.notApproved', query: this.toQuery(userData) })
  }

  public goToReopenAccountRoute() {
    return this.routerService.replace(this.reopenAccountRoute)
  }

  public goToAccountClosedRoute() {
    return this.routerService.replace(this.accountClosedRoute)
  }

  public goToAccountUnderReviewRoute(userData?: RestrictionPageUserData) {
    return this.routerService.replace({ name: 'auth.login.review', query: this.toQuery(userData) })
  }

  public getPersonalUseRoute(): TmRoutes {
    return this.loginPersonalRoute
  }

  private toQuery(userData?: RestrictionPageUserData): RestrictionPageQuery | undefined {
    const query: RestrictionPageQuery = {}
    if (userData?.fullName) {
      query.fullName = userData.fullName
    }
    if (userData?.email) {
      query.email = userData.email
    }
    return Object.keys(query).length ? query : undefined
  }
}
